import { useState } from 'react';
import {
	Box,
	Button,
	Grid,
	TextField,
	Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BOOKING_INTERLOCK_OVERRIDE, DARK_MODE, forbiddenChars } from '../../constants';
import { pxToRem, responsiveFontSizes } from '../../utils/getFontValue';
import ModalDialog from '../ModalDialog/ModalDialog';
import { useResolutionScale } from '../../hooks/useResolutionScale';
import { useSelector } from 'react-redux';

const classNames = {
	content: {
		width: '100%',
		padding: 4,
		// backgroundColor: ({ palette }) => (palette.mode === DARK_MODE ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
		borderRadius: 2,
	},
	heading: {
		color: '#FFA222',
		fontSize: pxToRem(12),
		...responsiveFontSizes({ sm: 12, md: 14, lg: 18 }),
		fontWeight: 600,
		fontFamily: "'Uni Neue', sans-serif",
	},
	item: {
		paddingTop: '0.3rem',
		paddingBottom: '0.3rem',
	},
	actionBtnContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 2,
		width: '100%',
		marginTop: 4,
		paddingRight: 4,
	},
	cancelBtn: {
		color: '#4D93E3',
		fontWeight: 600,
		fontSize: pxToRem(16),
		fontFamily: "'Uni Neue', sans-serif",
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.1)',
		},
	},
	textInput: {
		background: ({ palette }) => (palette.mode === DARK_MODE ? 'rgba(255, 255, 255, 0.05)' : '#E8F5FF'),
		outline: 'none',
		padding: 1.5,
		borderRadius: 1,
		'& .MuiInputBase-input': {
			fontSize: pxToRem(18),
			fontFamily: "'Uni Neue', sans-serif",
		},
	},
};

const ApproveButton = styled(Button)(({ theme }) => ({
	padding: '0.8rem 2.5rem',
	fontSize: pxToRem(16),
	fontWeight: 600,
	fontFamily: "'Uni Neue', sans-serif",
	borderRadius: '8px',
	background: 'linear-gradient(90deg, #0F107B 0%, #1517D0 100%)',
	color: '#fff',
	'&:hover': {
		background: 'linear-gradient(90deg, #0A0A5A 0%, #0F107B 100%)',
	},
	'&.Mui-disabled': {
		color: 'rgba(255, 255, 255, 0.5)',
		backgroundColor: theme.palette.mode === DARK_MODE ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
		background: 'none',
	},
}));

const ExceptionPopup = ({ handleSubmit, handleClose, notice }) => {
	const themeMode = useSelector((state) => state?.theme?.themeMode);
	const isDark = themeMode === DARK_MODE;
	const { scaleValue } = useResolutionScale();

	const [userData, setUserData] = useState({
		username: '',
		password: '',
		action: BOOKING_INTERLOCK_OVERRIDE,
		remark: '',
	});

	const isApproveDisabled = () => {
		return !(userData.username && userData.password && userData.remark);
	};

	return (
		<ModalDialog
			open={true}
			title="User Credentials"
			onClose={handleClose}
			modalWidth={scaleValue(600)}
			modalHeight="auto"
		>
			<Box sx={classNames.content}>
				<Grid container direction="column" spacing={3}>
					{notice ? (
						<Grid item>
							<Typography
								sx={{
									...classNames.heading,
									color: isDark ? '#FFCDD2' : '#C62828',
									whiteSpace: 'pre-wrap',
								}}
							>
								{notice}
							</Typography>
						</Grid>
					) : null}
					<Grid item>
						<Typography sx={classNames.heading}>
							Please ask quality person to login and approve the exception
						</Typography>
					</Grid>
					<Grid item container direction="column" spacing={1}>
						<Grid item>
							<Typography sx={classNames.heading}>User ID</Typography>
						</Grid>
						<Grid item>
							<TextField
								fullWidth
								autoFocus
								type="text"
								placeholder="Enter User ID"
								variant="standard"
								sx={classNames.textInput}
								value={userData.username}
								onChange={(e) => {
									const sanitizedValue = e.target.value.replace(forbiddenChars, '');
									setUserData({ ...userData, username: sanitizedValue });
								}}
								InputProps={{
									disableUnderline: true,
								}}
							/>
						</Grid>
					</Grid>
					<Grid item container direction="column" spacing={1}>
						<Grid item>
							<Typography sx={classNames.heading}>Password</Typography>
						</Grid>
						<Grid item>
							<TextField
								fullWidth
								type="password"
								variant="standard"
								placeholder="Enter Password"
								value={userData.password}
								sx={classNames.textInput}
								onChange={(e) => {
									const sanitizedValue = e.target.value.replace(forbiddenChars, '');
									setUserData({ ...userData, password: sanitizedValue });
								}}
								InputProps={{ disableUnderline: true }}
							/>
						</Grid>
					</Grid>
					<Grid item container direction="column" spacing={1}>
						<Grid item>
							<Typography sx={classNames.heading}>Remarks</Typography>
						</Grid>
						<Grid item>
							<TextField
								fullWidth
								multiline
								rows={3}
								type="text"
								variant="standard"
								placeholder="Enter Remarks"
								sx={classNames.textInput}
								value={userData.remark}
								onChange={(e) => {
									const sanitizedValue = e.target.value.replace(forbiddenChars, '');
									setUserData({ ...userData, remark: sanitizedValue });
								}}
								InputProps={{ disableUnderline: true }}
							/>
						</Grid>
					</Grid>
				</Grid>

				<Box sx={classNames.actionBtnContainer}>
					<Button sx={classNames.cancelBtn} onClick={handleClose}>
						CANCEL
					</Button>
					<ApproveButton
						disabled={isApproveDisabled()}
						onClick={() => handleSubmit(userData)}
					>
						APPROVE
					</ApproveButton>
				</Box>
			</Box>
		</ModalDialog>
	);
};

export default ExceptionPopup;
